import { useCallback } from 'react';
import { EXPLORER_DATAGRID_PAGESIZE, ExplorerItem, ExplorerItems } from '@/types/types';
import useExplorerItems from "@/common/hooks/useExplorerItems.ts";
import useDataGridApi from "@/common/hooks/useDataGridApi.ts";
import { EXPLORER_ROOT } from "@/features/Explorer/Explorer.tsx";
import { fetchExplorerItems, fetchRootExplorerItems } from "@/services/api/explorer-item.ts";
import { useBandWorkspace } from "@/client/provider/band-workspace-context-provider.tsx";

type FetchOptions = {
  offset?: number;
  limit?: number;
};

type UseRefetchPlaylistReturn = {
  fetchItems: (playlistId: string, options?: FetchOptions) => Promise<ExplorerItems>;
};

const useFetchPlaylistItems = (): UseRefetchPlaylistReturn => {
  const [dataGridApi] = useDataGridApi();
  const [, setExplorerItems] = useExplorerItems();
  const bandWorkspace = useBandWorkspace();
  const resetRows = useCallback((items: Record<string, ExplorerItem>, playlistId: string) => {
    dataGridApi?.current?.setRows(Object.values(items));

    setExplorerItems((prev: Record<string, ExplorerItem>) => {
      return Object.values(prev)
        .filter(item => item.explorerItemParentId !== playlistId)
        .reduce((acc, item) => {
          acc[item.id] = item;
          return acc;
        }, {} as Record<string, ExplorerItem>);
    });

    setExplorerItems(prev => ({
      ...prev,
      ...items,
    }));
  }, [
    dataGridApi,
    setExplorerItems
  ]);

  const fetchItems = useCallback(
    async (playlistId: string, options?: FetchOptions): Promise<ExplorerItems> => {
      if (!playlistId) {
        throw new Error('Playlist ID is required');
      }

      const offset = options?.offset ?? 0;
      const limit = Math.max(options?.limit || 0, EXPLORER_DATAGRID_PAGESIZE);

      dataGridApi?.current?.setLoading(true);

      try {
        const items: ExplorerItems = await (playlistId === EXPLORER_ROOT
          ? fetchRootExplorerItems({
            offset,
            limit,
            bandWorkspaceId: bandWorkspace?.id,
          })
          : fetchExplorerItems({
            folderId: playlistId,
            offset,
            limit,
            bandWorkspaceId: bandWorkspace?.id,
          }));

        resetRows(items, playlistId);

        return items;
      } catch (e) {
        console.log(e);
        throw e;
      } finally {
        dataGridApi?.current?.setLoading(false);
      }
    },
    [
      dataGridApi,
      resetRows,
      bandWorkspace?.id
    ]
  );

  return { fetchItems };
};

export default useFetchPlaylistItems;
